// =============================================================================
// SaaS-Admin › Tenants
// Live data from GET /saastracker/admin/tenants. Search, plan/status filters,
// sorting and paging all run server-side; the row shape comes back pre-computed
// so nothing here has to add up a ledger.
//
// Columns: Name · Email · Plan · Status · Credit usage · Signed up · Action
//
// This is a PREPAID CREDIT model, so "credit usage" is totalUsed / totalGranted
// (lifetime), not a monthly quota, and there is no MRR column — `totalPaid` in
// the detail drawer is the real money figure.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../services/adminDataService';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: api.TenantStatus[] = ['Trial', 'Active', 'Deactivated', 'Suspended'];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'usage',   label: 'Credit usage' },
  { value: 'newest',  label: 'Newest first' },
  { value: 'oldest',  label: 'Oldest first' },
  { value: 'name',    label: 'Name (A–Z)' },
  { value: 'balance', label: 'Lowest balance' },
  { value: 'paid',    label: 'Highest spend' },
];

// ---- formatting -------------------------------------------------------------

const fmtCredits = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const fmtMoney = (n: number | null | undefined, ccy: string = 'USD'): string =>
  `${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * The bar encodes utilisation and nothing else, on the same thresholds the app's
 * own banner uses. Lifecycle is the status pill's job — folding it in here
 * painted a suspended tenant at 24% used the same red as one that had run dry.
 */
const barClass = (pctUsed: number): string =>
  pctUsed >= 90 ? 'danger' : pctUsed >= 75 ? 'warn' : '';

const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

// ---- component --------------------------------------------------------------

export const Tenants: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('usage');
  const [page, setPage] = useState(1);

  const [data, setData] = useState<api.AdminTenantListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  // Plan is a free-text column on Tenant (stamped from companyType at first
  // login), so the filter options are discovered from what comes back rather
  // than hardcoded to a list that would drift.
  const [knownPlans, setKnownPlans] = useState<string[]>([]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [detail, setDetail] = useState<api.AdminTenantDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const flash = (m: string) => { setErr(''); setMsg(m); window.setTimeout(() => setMsg(''), 2500); };
  const fail = (e: any) => { setMsg(''); setErr(e?.message ?? String(e)); };

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Guards against a slow early response overwriting a newer one.
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setErr('');
    try {
      const res = await api.getTenants({ search, plan, status, sort, page, pageSize: PAGE_SIZE });
      if (id !== reqId.current) return;
      setData(res);
      setKnownPlans(prev => Array.from(
        new Set([...prev, ...res.items.map(i => i.plan).filter((p): p is string => !!p)]),
      ).sort());
    } catch (e: any) {
      if (id !== reqId.current) return;
      setData(null);
      fail(e);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [search, plan, status, sort, page]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setSearchInput(''); setSearch(''); setPlan('all'); setStatus('all'); setSort('usage'); setPage(1);
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await api.getTenantDetail(id));
    } catch (e) {
      fail(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleHold = async (t: api.AdminTenantRow) => {
    const next: api.TenantStatus = t.status === 'Suspended' ? 'Active' : 'Suspended';
    const verb = next === 'Suspended' ? 'Suspend' : 'Release';
    if (!window.confirm(`${verb} “${t.name}”?`)) return;

    setBusyId(t.id);
    try {
      await api.setTenantStatus(t.id, next);
      flash(next === 'Suspended' ? `${t.name} suspended` : `${t.name} released`);
      await load();
      if (detail?.tenant.id === t.id) await openDetail(t.id);
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = await api.getAllTenants({ search, plan, status, sort });
      const head = [
        'Tenant', 'Email', 'Phone', 'Plan', 'Status', 'Credits used', 'Credits granted',
        '% used', 'Balance', 'Currency', 'Lifetime paid', 'Signed up', 'Trial ends', 'Last activity',
      ];
      const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [
        head.join(','),
        ...rows.map(r => [
          r.name, r.email, r.phone, r.plan, r.status,
          r.totalUsed, r.totalGranted, r.pctUsed, r.balance, r.currency, r.totalPaid,
          r.createdAt, r.trialEndsAt, r.lastActivityAt,
        ].map(esc).join(',')),
      ].join('\n');

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenants-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      flash(`Exported ${rows.length} tenants`);
    } catch (e) {
      fail(e);
    } finally {
      setExporting(false);
    }
  };

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstOnPage = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Tenants</h1>
          <p className="ov-header__sub">
            {loading && !data
              ? 'Loading workspaces…'
              : `All workspaces on Logipulse — ${total.toLocaleString('en-US')} match${total === 1 ? 'es' : ''} the current filters.`}
          </p>
        </div>
        <div className="ov-header__actions">
          <button className="btn-small" onClick={load} disabled={loading}>
            <span className="material-icons">refresh</span> Refresh
          </button>
          <button className="btn-small" onClick={exportCsv} disabled={exporting || total === 0}>
            <span className="material-icons">file_download</span> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </header>

      {(msg || err) && (
        <div style={{
          margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: err ? '#fdecea' : '#e7f6ec', color: err ? '#c0392b' : '#1e7e46',
          border: `1px solid ${err ? '#f5c6cb' : '#bfe6cd'}`,
        }}>
          {err || msg}
        </div>
      )}

      {/* Filters — all applied server-side */}
      <div className="filters">
        <div className="filters__search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder="Search by name, email, or phone…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>

        <div className="filters__group">
          <label>Plan</label>
          <select value={plan} onChange={e => { setPlan(e.target.value); setPage(1); }}>
            <option value="all">All plans</option>
            {knownPlans.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="filters__group">
          <label>Status</label>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="filters__group">
          <label>Sort by</label>
          <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <button className="btn-small btn-small--ghost" onClick={resetFilters}>Reset</button>
      </div>

      {/* Tenants table */}
      <div className="tenants-table">
        <div className="tenants-table__head">
          <div>Name</div><div>Email</div><div>Plan</div><div>Status</div>
          <div>Credit usage</div><div>Signed up</div><div>Action</div>
        </div>

        {items.map(t => (
          <div className="tenant-row" key={t.id}>
            <div className="tenant-row__main">
              <div className="tenant-row__name">{t.name || <span style={{ color: '#9aa7b4', fontWeight: 400 }}>No name</span>}</div>
              {t.phone && (
                <div className="tenant-row__contact">
                  <span className="material-icons">phone</span>
                  {t.phone}
                </div>
              )}
            </div>

            <div className="tenant-row__email" style={{ minWidth: 0, overflowWrap: 'anywhere', fontSize: 13 }}>
              {t.email || <span style={{ color: '#9aa7b4' }}>No email on file</span>}
            </div>

            <div>
              <span className={`plan-tag plan-tag--${t.plan ? slug(t.plan) : 'none'}`}>
                {t.plan ? t.plan.toUpperCase() : 'NONE'}
              </span>
            </div>

            <div>
              <span className={`status-pill status-pill--${slug(t.status)}`}>{t.status}</span>
            </div>

            <div>
              <div className="usage-mini">
                <div className="usage-mini__bar">
                  <div
                    className={barClass(t.pctUsed)}
                    style={{ width: `${Math.min(100, Math.max(0, t.pctUsed))}%` }}
                  />
                </div>
                <div className="usage-mini__num">
                  {fmtCredits(t.totalUsed)} / {fmtCredits(t.totalGranted)}
                  <span>· {t.pctUsed}%</span>
                </div>
              </div>
            </div>

            <div className="tenant-row__date">{fmtDate(t.createdAt)}</div>

            <div style={{ gap: 4 }}>
              <button className="btn-small" onClick={() => openDetail(t.id)}>View</button>
              <button
                className="btn-small btn-small--ghost"
                onClick={() => toggleHold(t)}
                disabled={busyId === t.id}
                title={t.status === 'Suspended'
                  ? 'Release the manual hold — status reverts to whatever credit and trial state say'
                  : 'Place a manual hold — blocks the tenant regardless of credit'}
              >
                {busyId === t.id ? '…' : t.status === 'Suspended' ? 'Release' : 'Suspend'}
              </button>
            </div>
          </div>
        ))}

        {loading && items.length === 0 && (
          <div className="empty">
            <span className="material-icons">hourglass_empty</span>
            Loading tenants…
          </div>
        )}

        {!loading && items.length === 0 && !err && (
          <div className="empty">
            <span className="material-icons">search_off</span>
            No tenants match your filters.
          </div>
        )}

        {!loading && err && (
          <div className="empty">
            <span className="material-icons">cloud_off</span>
            Could not reach the SaaS Tracker API.
            <button className="btn-small" onClick={load}>Retry</button>
          </div>
        )}
      </div>

      {/* Paging */}
      {total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 12, fontSize: 12, color: '#6b7684',
        }}>
          <span>Showing {firstOnPage}–{lastOnPage} of {total.toLocaleString('en-US')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-small" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              <span className="material-icons">chevron_left</span> Prev
            </button>
            <span>Page {page} of {pageCount}</span>
            <button className="btn-small" disabled={page >= pageCount || loading} onClick={() => setPage(p => p + 1)}>
              Next <span className="material-icons">chevron_right</span>
            </button>
          </span>
        </div>
      )}

      {(detail || detailLoading) && (
        <TenantDrawer
          detail={detail}
          loading={detailLoading}
          onClose={() => { setDetail(null); setDetailLoading(false); }}
          onAdjusted={(id, m) => { flash(m); openDetail(id); load(); }}
        />
      )}
    </div>
  );
};

// ---- detail drawer ----------------------------------------------------------

const dl: React.CSSProperties = { fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.04em' };
const dv: React.CSSProperties = { fontSize: 15, fontWeight: 600, color: '#173042' };
const cell: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #eef2f6', fontSize: 12 };
const hcell: React.CSSProperties = {
  ...cell, color: '#6b7684', fontSize: 10, textTransform: 'uppercase',
  letterSpacing: '.04em', fontWeight: 600, textAlign: 'left',
};

/** Ledger entry types as stored by the API (enum order), for rows that arrive as numbers. */
const LEDGER_TYPES = ['Grant', 'Purchase', 'Deduct', 'Refund', 'Expiry', 'Adjustment'];
const ledgerType = (t: string | number): string =>
  typeof t === 'number' ? (LEDGER_TYPES[t] ?? String(t)) : String(t ?? '');

/** Payment statuses as stored by the API (enum order), for rows that arrive as numbers. */
const TX_STATUSES = ['Created', 'Pending', 'Completed', 'Failed', 'Refunded'];
const txStatus = (s: string | number): string =>
  typeof s === 'number' ? (TX_STATUSES[s] ?? String(s)) : String(s ?? '');

/** A fresh id per adjustment, so a double click is applied once. */
const newRequestId = (): string =>
  (window.crypto && 'randomUUID' in window.crypto)
    ? (window.crypto as any).randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Add or remove credits on the tenant's wallet: refunds (e.g. a double charge),
 * goodwill credit, corrections. Saved as an "Adjustment" ledger entry carrying the
 * reason and the admin's email. Two steps (Review, then Confirm) because this changes
 * a customer's balance.
 */
const AdjustCredits: React.FC<{
  tenant: api.AdminTenantRow;
  onDone: (tenantId: string, message: string) => void;
}> = ({ tenant, onDone }) => {
  const [direction, setDirection] = useState<'add' | 'remove'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState(newRequestId);

  const value = Number(amount);
  const invalid =
    !amount || isNaN(value) || value <= 0 ? 'Enter an amount above zero.'
    : reason.trim().length < 5 ? 'Enter a reason (at least 5 characters).'
    : direction === 'remove' && value > tenant.balance ? `The tenant has only ${fmtCredits(tenant.balance)} credits.`
    : '';

  const reset = () => {
    setAmount(''); setReason(''); setConfirming(false); setError(''); setRequestId(newRequestId());
  };

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      const signed = direction === 'add' ? value : -value;
      const r = await api.adjustTenantCredits(tenant.id, signed, reason.trim(), requestId);
      const verb = direction === 'add' ? 'Added' : 'Removed';
      reset();
      onDone(tenant.id, r.alreadyApplied
        ? 'This adjustment was already applied; nothing changed.'
        : `${verb} ${fmtCredits(value)} credits. New balance: ${fmtCredits(r.balance)}.`);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const newBalance = tenant.balance + (direction === 'add' ? (value || 0) : -(value || 0));
  const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5563', marginBottom: 6 };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #cdd6e0',
    borderRadius: 8, fontSize: 14, color: '#173042', background: '#fff',
  };
  const toggle = (on: boolean, tone: 'add' | 'remove'): React.CSSProperties => ({
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: confirming || busy ? 'default' : 'pointer',
    border: 'none', borderRadius: 7,
    background: on ? (tone === 'add' ? '#e7f6ec' : '#fdecea') : 'transparent',
    color: on ? (tone === 'add' ? '#1e7e46' : '#c0392b') : '#6b7684',
    boxShadow: on ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
  });
  const tileLabel: React.CSSProperties = { fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.04em' };

  return (
    <div>
      {/* Balance now and after */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 10, background: '#f6f8fa' }}>
          <div style={tileLabel}>Current balance</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#173042', marginTop: 2 }}>{fmtCredits(tenant.balance)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', color: '#9aa7b4' }}>
          <span className="material-icons">arrow_forward</span>
        </div>
        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 10, background: value > 0 ? (direction === 'add' ? '#e7f6ec' : '#fdecea') : '#f6f8fa' }}>
          <div style={tileLabel}>Balance after</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#173042', marginTop: 2 }}>{fmtCredits(newBalance)}</div>
        </div>
      </div>

      {/* Add or remove */}
      <label style={label}>Type</label>
      <div style={{ display: 'flex', gap: 4, padding: 4, borderRadius: 9, background: '#eef2f6', marginBottom: 16 }}>
        <button type="button" style={toggle(direction === 'add', 'add')} disabled={confirming || busy} onClick={() => setDirection('add')}>
          <span className="material-icons" style={{ fontSize: 18 }}>add_circle</span> Add credits
        </button>
        <button type="button" style={toggle(direction === 'remove', 'remove')} disabled={confirming || busy} onClick={() => setDirection('remove')}>
          <span className="material-icons" style={{ fontSize: 18 }}>remove_circle</span> Remove credits
        </button>
      </div>

      <label style={label} htmlFor="adj-amount">Credits</label>
      <input id="adj-amount" style={{ ...input, marginBottom: 16 }} type="number" min="0" step="0.1" placeholder="0"
             value={amount} disabled={confirming || busy} onChange={e => setAmount(e.target.value)} />

      <label style={label} htmlFor="adj-reason">Reason</label>
      <textarea id="adj-reason" style={{ ...input, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }} maxLength={300}
                placeholder="e.g. Refund of double charge on 12 Sep shipments"
                value={reason} disabled={confirming || busy} onChange={e => setReason(e.target.value)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b96a3', marginTop: 4 }}>
        <span>Saved on the credit ledger with your email.</span>
        <span>{reason.length}/300</span>
      </div>

      {(amount || reason) && invalid && !confirming && (
        <div style={{ fontSize: 12, color: '#b26a00', marginTop: 12 }}>{invalid}</div>
      )}
      {error && (
        <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 8, background: '#fdecea', color: '#c0392b', fontSize: 13 }}>{error}</div>
      )}

      {/* Confirmation */}
      {confirming && (
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: '#fff8e6', border: '1px solid #f0c36d', fontSize: 13, color: '#5c4400', lineHeight: 1.5 }}>
          <strong>Please confirm.</strong> {direction === 'add' ? 'Add' : 'Remove'} <strong>{fmtCredits(value)}</strong> credits
          {direction === 'add' ? ' to ' : ' from '}<strong>{tenant.name}</strong>. The balance will be <strong>{fmtCredits(newBalance)}</strong>.
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 14, borderTop: '1px solid #eef2f6' }}>
        {!confirming ? (
          <button className="btn-small btn-small--primary" disabled={!!invalid || busy} onClick={() => setConfirming(true)}>
            Review
          </button>
        ) : (
          <>
            <button className="btn-small btn-small--ghost" disabled={busy} onClick={() => setConfirming(false)}>Back</button>
            <button className="btn-small btn-small--primary" disabled={busy} onClick={apply}>
              {busy ? 'Applying...' : `Confirm ${direction === 'add' ? 'add' : 'remove'}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const TenantDrawer: React.FC<{
  detail: api.AdminTenantDetail | null;
  loading: boolean;
  onClose: () => void;
  onAdjusted: (tenantId: string, message: string) => void;
}> = ({ detail, loading, onClose, onAdjusted }) => {
  /** The detail popup open over the drawer, if any. */
  const [popup, setPopup] = useState<DrawerPopup | null>(null);

  // Escape closes the popup first, then the drawer, matching the console's overlays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (popup) setPopup(null); else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, popup]);

  const t = detail?.tenant;
  const ledger = detail?.ledger ?? [];
  const purchases = detail?.transactions ?? [];
  const invoices = detail?.invoices ?? [];
  const reminders = detail?.notifications ?? [];

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,32,45,.35)', zIndex: 10000, display: 'flex', justifyContent: 'flex-end' }}
    >
      <aside
        onClick={e => e.stopPropagation()}
        style={{ width: 'min(680px, 94vw)', background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-8px 0 24px rgba(0,0,0,.12)' }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #e3e9ef', position: 'sticky', top: 0, background: '#fff',
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#173042' }}>{t?.name ?? 'Tenant'}</div>
            <div style={{ fontSize: 12, color: '#6b7684' }}>{t?.email ?? (loading ? 'Loading…' : '—')}</div>
          </div>
          <button className="btn-small btn-small--ghost" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </header>

        {loading && <div style={{ padding: 32, color: '#6b7684', fontSize: 13 }}>Loading tenant…</div>}

        {t && (
          <div style={{ padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 16, marginBottom: 22 }}>
              <div>
                <div style={dl}>Balance</div>
                <div style={dv}>{fmtMoney(t.balance, t.currency)}</div>
              </div>
              <div>
                <div style={dl}>Credit used</div>
                <div style={dv}>{fmtCredits(t.totalUsed)} / {fmtCredits(t.totalGranted)}</div>
              </div>
              <div>
                <div style={dl}>Utilisation</div>
                <div style={dv}>{t.pctUsed}%</div>
              </div>
              <div>
                <div style={dl}>Lifetime paid</div>
                <div style={dv}>{fmtMoney(t.totalPaid, t.currency)}</div>
              </div>
              <div>
                <div style={dl}>Purchases</div>
                <div style={dv}>{t.purchaseCount}</div>
              </div>
              <div>
                <div style={dl}>Status</div>
                <div style={dv}>
                  <span className={`status-pill status-pill--${slug(t.status)}`}>{t.status}</span>
                </div>
              </div>
              <div>
                <div style={dl}>Signed up</div>
                <div style={dv}>{fmtDate(t.createdAt)}</div>
              </div>
              <div>
                <div style={dl}>Trial ends</div>
                <div style={dv}>
                  {t.trialEndsAt ? `${fmtDate(t.trialEndsAt)}${t.trialDaysLeft > 0 ? ` (${t.trialDaysLeft}d left)` : ''}` : '—'}
                </div>
              </div>
              <div>
                <div style={dl}>Last activity</div>
                <div style={dv}>{fmtDateTime(t.lastActivityAt)}</div>
              </div>
            </div>

            {/* Details open in a popup, so the drawer stays short */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 10 }}>
              <DrawerButton icon="tune" label="Adjust credits" hint="Refund, goodwill, correction"
                            primary onClick={() => setPopup('adjust')} />
              <DrawerButton icon="history" label="Credit history"
                            hint="Credits used by actions, as the tenant sees it" onClick={() => setPopup('history')} />
              <DrawerButton icon="list_alt" label="User actions"
                            hint="Every action by month, billable or not" onClick={() => setPopup('actions')} />
              <DrawerButton icon="sell" label="Custom pricing"
                            hint="This tenant's own credit price for selected actions" onClick={() => setPopup('pricing')} />
              <DrawerButton icon="account_balance_wallet" label="Credit ledger" count={ledger.length}
                            hint="Every credit in and out" onClick={() => setPopup('ledger')} />
              <DrawerButton icon="shopping_cart" label="Purchases" count={purchases.length}
                            hint="Top-ups and their status" onClick={() => setPopup('purchases')} />
              <DrawerButton icon="receipt_long" label="Invoices" count={invoices.length}
                            hint="Issued receipts" onClick={() => setPopup('invoices')} />
              <DrawerButton icon="campaign" label="Reminders sent" count={reminders.length}
                            hint="Emails and in-app notices" onClick={() => setPopup('reminders')} />
            </div>
          </div>
        )}
      </aside>

      {t && popup && (
        <Popup
          icon={POPUP_META[popup].icon}
          title={POPUP_META[popup].title}
          subtitle={t.name}
          wide={popup !== 'adjust'}
          onClose={() => setPopup(null)}
        >
          {popup === 'adjust' && (
            <AdjustCredits tenant={t} onDone={(id, m) => { setPopup(null); onAdjusted(id, m); }} />
          )}

          {popup === 'history' && <TenantCreditHistory tenant={t} />}

          {popup === 'actions' && <TenantUserActions tenant={t} />}

          {popup === 'pricing' && <TenantPricing tenant={t} />}

          {popup === 'ledger' && (
            <DataTable
              head={['When', 'Type', 'Action or reason', 'Amount', 'Balance after']}
              align={['left', 'left', 'left', 'right', 'right']}
              rows={ledger.map(e => [
                fmtDateTime(e.createdAt),
                <span className="status-pill" style={pillStyle(ledgerType(e.type as any))}>{ledgerType(e.type as any)}</span>,
                <span title={e.reference ?? undefined}>{e.actionKey ?? e.reference ?? ''}</span>,
                <span style={{ color: e.amount < 0 ? '#c0392b' : '#1e7e46', fontWeight: 600 }}>
                  {e.amount > 0 ? '+' : ''}{fmtCredits(e.amount)}
                </span>,
                fmtCredits(e.balanceAfter),
              ])}
              empty="No credit movement yet."
              note={ledger.length >= 100 ? 'Showing the latest 100 entries.' : undefined}
            />
          )}

          {popup === 'purchases' && (
            <DataTable
              head={['When', 'Provider', 'Amount', 'Credits', 'Status']}
              align={['left', 'left', 'right', 'right', 'left']}
              rows={purchases.map(x => [
                fmtDateTime(x.createdAt), x.provider,
                fmtMoney(x.amount, x.currency), fmtCredits(x.credits),
                <span className="status-pill" style={pillStyle(txStatus(x.status as any))}>{txStatus(x.status as any)}</span>,
              ])}
              empty="No purchases yet. The tenant is still on free credit."
            />
          )}

          {popup === 'invoices' && (
            <DataTable
              head={['Number', 'Issued', 'Amount', 'Emailed']}
              align={['left', 'left', 'right', 'left']}
              rows={invoices.map(i => [
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{i.number}</span>,
                fmtDate(i.createdAt), fmtMoney(i.amount, i.currency), i.emailedAt ? fmtDateTime(i.emailedAt) : 'Not emailed',
              ])}
              empty="No invoices issued."
            />
          )}

          {popup === 'reminders' && (
            <DataTable
              head={['When', 'Event', 'Title', 'Read']}
              align={['left', 'left', 'left', 'left']}
              rows={reminders.map(n => [
                fmtDateTime(n.createdAt), n.event, n.title,
                n.readAt ? <span style={{ color: '#1e7e46' }}>Read</span> : <span style={{ color: '#6b7684' }}>Unread</span>,
              ])}
              empty="No reminders sent to this tenant."
            />
          )}
        </Popup>
      )}
    </div>
  );
};

// ---- drawer buttons and popups ----------------------------------------------

type DrawerPopup = 'adjust' | 'history' | 'actions' | 'pricing' | 'ledger' | 'purchases' | 'invoices' | 'reminders';

const POPUP_META: Record<DrawerPopup, { icon: string; title: string }> = {
  adjust:    { icon: 'tune',                   title: 'Adjust credits' },
  history:   { icon: 'history',                title: 'Credit history' },
  actions:   { icon: 'list_alt',               title: 'User actions' },
  pricing:   { icon: 'sell',                   title: 'Custom pricing' },
  ledger:    { icon: 'account_balance_wallet', title: 'Credit ledger' },
  purchases: { icon: 'shopping_cart',          title: 'Purchases' },
  invoices:  { icon: 'receipt_long',           title: 'Invoices' },
  reminders: { icon: 'campaign',               title: 'Reminders sent' },
};

/** Colours for ledger types and payment statuses shown as pills. */
const pillStyle = (value: unknown): React.CSSProperties => {
  const v = String(value ?? '').toLowerCase();
  const [bg, fg] =
    v === 'deduct' || v === 'failed' || v === 'expiry' ? ['#fdecea', '#c0392b']
    : v === 'purchase' || v === 'completed' || v === 'grant' ? ['#e7f6ec', '#1e7e46']
    : v === 'adjustment' || v === 'refund' || v === 'refunded' ? ['#e8f1fb', '#1d5fa8']
    : ['#f1f4f7', '#4a5563'];
  return { background: bg, color: fg, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 };
};

const DrawerButton: React.FC<{
  icon: string;
  label: string;
  hint: string;
  count?: number;
  primary?: boolean;
  onClick: () => void;
}> = ({ icon, label, hint, count, primary, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer',
      padding: '12px 14px', borderRadius: 10,
      border: `1px solid ${primary ? '#9cc9ea' : '#e3e9ef'}`,
      background: primary ? '#f0f7fd' : '#fff',
    }}
  >
    <span className="material-icons" style={{ fontSize: 22, color: primary ? '#1d5fa8' : '#4a6a85' }}>{icon}</span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#173042' }}>
        {label}
        {count !== undefined && (
          <span style={{ marginLeft: 6, padding: '0 7px', borderRadius: 9, background: '#eef2f6', color: '#4a5563', fontSize: 11, fontWeight: 600 }}>
            {count}
          </span>
        )}
      </span>
      <span style={{ display: 'block', fontSize: 11, color: '#6b7684', marginTop: 2 }}>{hint}</span>
    </span>
    <span className="material-icons" style={{ fontSize: 18, color: '#9aa7b4' }}>chevron_right</span>
  </button>
);

/** Centred popup over the drawer. Clicking outside or pressing Escape closes it. */
const Popup: React.FC<{
  icon: string;
  title: string;
  subtitle?: string;
  wide?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, wide, onClose, children }) => (
  <div
    onClick={e => { e.stopPropagation(); onClose(); }}
    style={{
      position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(15,32,45,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
  >
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={e => e.stopPropagation()}
      style={{
        width: wide ? 'min(880px, 96vw)' : 'min(560px, 96vw)', maxHeight: '86vh',
        display: 'flex', flexDirection: 'column',
        background: '#fff', borderRadius: 12, boxShadow: '0 18px 48px rgba(0,0,0,.22)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #e3e9ef' }}>
        <span className="material-icons" style={{ fontSize: 22, color: '#1d5fa8' }}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#173042' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: '#6b7684' }}>{subtitle}</div>}
        </div>
        <button className="btn-small btn-small--ghost" onClick={onClose} aria-label="Close">
          <span className="material-icons">close</span>
        </button>
      </header>
      <div style={{ padding: 18, overflowY: 'auto' }}>{children}</div>
    </div>
  </div>
);

/** Table used inside the popups: sticky header, zebra rows, numbers aligned right. */
const DataTable: React.FC<{
  head: string[];
  align: ('left' | 'right')[];
  rows: React.ReactNode[][];
  empty: string;
  note?: string;
}> = ({ head, align, rows, empty, note }) =>
  rows.length === 0 ? (
    <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: '#8b96a3' }}>{empty}</div>
  ) : (
    <>
      <div style={{ border: '1px solid #e3e9ef', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {head.map((h, j) => (
                <th key={h} style={{ ...hcell, position: 'sticky', top: 0, background: '#f6f8fa', textAlign: align[j] ?? 'left', padding: '9px 12px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 ? '#fbfcfd' : '#fff' }}>
                {r.map((c, j) => (
                  <td key={j} style={{ ...cell, textAlign: align[j] ?? 'left', padding: '9px 12px', fontSize: 13 }}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {note && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 8 }}>{note}</div>}
    </>
  );

// ---- tenant credit history (same list the tenant sees in Billing and credits) ----

type HistoryRange = 'thisMonth' | '7d' | 'lastMonth' | '3m' | 'custom';
const HISTORY_MAX_MONTHS = 3;
const HISTORY_PAGE_SIZE = 25;

/** The period as local dates [from, to), like the main application. */
const historyDates = (range: HistoryRange, fromDate: string, toDate: string): { from?: Date; to?: Date } => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
  const day = (yy: number, mm: number, dd: number) => new Date(yy, mm, dd);
  const parse = (v: string) => {
    const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
    return r ? new Date(+r[1], +r[2] - 1, +r[3]) : undefined;
  };
  switch (range) {
    case 'thisMonth': return { from: day(y, m, 1), to: day(y, m, d + 1) };
    case '7d':        return { from: day(y, m, d - 6), to: day(y, m, d + 1) };
    case 'lastMonth': return { from: day(y, m - 1, 1), to: day(y, m, 1) };
    case '3m':        return { from: day(y, m - HISTORY_MAX_MONTHS, d + 1), to: day(y, m, d + 1) };
    case 'custom': {
      const a = parse(fromDate), b = parse(toDate);
      return { from: a, to: b ? day(b.getFullYear(), b.getMonth(), b.getDate() + 1) : undefined };
    }
  }
  return {};
};

const shortDate = (x: Date) => x.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Readable name for an action key, as in the main application:
 * 'CreateShipment' -> 'Shipment created', 'shipment.create' -> 'Shipment created'.
 */
const actionLabel = (key: string): string => {
  const k = (key || '').trim();
  if (!k) return 'Usage';
  const pastTense: Record<string, string> = {
    create: 'created', update: 'updated', delete: 'deleted', generate: 'generated',
    cancel: 'cancelled', approve: 'approved', submit: 'submitted', post: 'posted',
    send: 'sent', add: 'added', remove: 'removed', import: 'imported',
    export: 'exported', print: 'printed', close: 'closed', save: 'saved',
  };
  if (k.includes('.')) {
    const [entity, verb] = k.split('.');
    const noun = entity ? entity.charAt(0).toUpperCase() + entity.slice(1) : k;
    return verb ? `${noun} ${pastTense[verb] ?? verb}` : noun;
  }
  const words = k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_-]+/).filter(w => w.length);
  if (!words.length) return k;
  const sentence = (parts: string[]) =>
    parts.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toLowerCase())).join(' ');
  const verb = pastTense[words[0].toLowerCase()];
  return verb && words.length > 1 ? `${sentence(words.slice(1))} ${verb}` : sentence(words);
};

/** Old rows can hold a user GUID instead of a name; a GUID means nothing to the reader. */
const displayUser = (v: string | null): string => {
  const s = (v ?? '').trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ? '' : s;
};

const TenantCreditHistory: React.FC<{ tenant: api.AdminTenantRow }> = ({ tenant }) => {
  const [range, setRange] = useState<HistoryRange>('thisMonth');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sort, setSort] = useState<'newest' | 'oldest' | 'credits_desc' | 'credits_asc'>('newest');
  const [page, setPage] = useState(1);
  /** The period of the list on screen (Custom dates apply only on Apply). */
  const [applied, setApplied] = useState<{ range: HistoryRange; fromDate: string; toDate: string }>({ range: 'thisMonth', fromDate: '', toDate: '' });
  const [data, setData] = useState<api.CreditHistoryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const todayIso = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  })();

  /** Why the Custom dates cannot be applied, or '' when they can. */
  const customError = (() => {
    if (range !== 'custom') return '';
    const { from, to } = historyDates('custom', fromDate, toDate);
    if (!from || !to) return 'Choose a From and a To date.';
    if (to <= from) return 'The To date must be on or after the From date.';
    const limit = new Date(from.getFullYear(), from.getMonth() + HISTORY_MAX_MONTHS, from.getDate());
    if (to > limit) return `You can view up to ${HISTORY_MAX_MONTHS} months at a time. Choose a shorter period.`;
    return '';
  })();

  const query = useCallback((p: number, exportAll: boolean): api.CreditHistoryQuery => {
    const { from, to } = historyDates(applied.range, applied.fromDate, applied.toDate);
    return {
      page: p, pageSize: HISTORY_PAGE_SIZE, sort,
      fromUtc: from?.toISOString(), toUtc: to?.toISOString(),
      export: exportAll || undefined,
    };
  }, [applied, sort]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    api.searchTenantCreditHistory(tenant.id, query(page, false))
      .then(r => { if (live) setData(r); })
      .catch(e => { if (live) { setData(null); setError(e?.message ?? String(e)); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tenant.id, query, page]);

  const changeRange = (r: HistoryRange) => {
    setRange(r);
    if (r !== 'custom') { setFromDate(''); setToDate(''); setApplied({ range: r, fromDate: '', toDate: '' }); setPage(1); }
  };
  const applyCustom = () => {
    if (customError) return;
    setApplied({ range: 'custom', fromDate, toDate });
    setPage(1);
  };

  /** Every row of the period (up to 10,000) as a CSV file. */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.searchTenantCreditHistory(tenant.id, query(1, true));
      const esc = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
      const lines = [['When', 'Action', 'Action key', 'User', 'Credits'].map(esc).join(',')];
      r.items.forEach(i => lines.push([
        new Date(i.at).toLocaleString(), actionLabel(i.actionKey), i.actionKey, displayUser(i.userRef), String(-i.creditCost),
      ].map(esc).join(',')));
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credit-history-${slug(tenant.name)}-${todayIso}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setExporting(false);
    }
  };

  const period = (() => {
    const { from, to } = historyDates(applied.range, applied.fromDate, applied.toDate);
    return from && to ? { from: shortDate(from), to: shortDate(new Date(to.getFullYear(), to.getMonth(), to.getDate() - 1)) } : null;
  })();

  const emptyText =
    applied.range === 'thisMonth' ? 'No credit usage this month yet.'
    : applied.range === '7d' ? 'No credits were used in the last 7 days.'
    : applied.range === 'lastMonth' ? 'No credits were used last month.'
    : applied.range === '3m' ? 'No credits were used in the last 3 months.'
    : period ? `No credits were used between ${period.from} and ${period.to}.` : 'No credits were used in this period.';

  const field: React.CSSProperties = { padding: '7px 10px', border: '1px solid #cdd6e0', borderRadius: 7, fontSize: 13, background: '#fff' };
  const fl: React.CSSProperties = { display: 'block', fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 };
  const stat = (icon: string, label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 22px 4px 0' }}>
      <span className="material-icons" style={{ fontSize: 20, color: '#1d5fa8' }}>{icon}</span>
      <div>
        <div style={fl}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#173042', whiteSpace: 'nowrap' }}>{value}</div>
      </div>
    </div>
  );
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        <label><span style={fl}>Period</span>
          <select style={field} value={range} onChange={e => changeRange(e.target.value as HistoryRange)}>
            <option value="thisMonth">This month</option>
            <option value="7d">Last 7 days</option>
            <option value="lastMonth">Last month</option>
            <option value="3m">Last 3 months</option>
            <option value="custom">Custom dates</option>
          </select>
        </label>
        {range === 'custom' && (
          <>
            <label><span style={fl}>From</span>
              <input style={field} type="date" max={todayIso} value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </label>
            <label><span style={fl}>To</span>
              <input style={field} type="date" max={todayIso} value={toDate} onChange={e => setToDate(e.target.value)} />
            </label>
            <button className="btn-small btn-small--primary" disabled={!!customError || loading} onClick={applyCustom}>Apply</button>
          </>
        )}
        <label><span style={fl}>Sort by</span>
          <select style={field} value={sort} onChange={e => { setSort(e.target.value as any); setPage(1); }}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="credits_desc">Most credits first</option>
            <option value="credits_asc">Fewest credits first</option>
          </select>
        </label>
        <span style={{ flex: 1 }} />
        <button className="btn-small btn-small--ghost" disabled={exporting || !data?.total} onClick={exportCsv}>
          <span className="material-icons">file_download</span> {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {range === 'custom' && customError && (fromDate || toDate) && (
        <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: '#fff8e6', border: '1px solid #f0c36d', color: '#8a5a00', fontSize: 13 }}>
          {customError}
        </div>
      )}

      {/* Summary */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 0 14px' }}>
        {period && stat('date_range', 'Period', <>{period.from} <span style={{ fontWeight: 400, color: '#6b7684' }}>to</span> {period.to}</>)}
        {stat('bolt', 'Actions', loading ? '...' : (data?.total ?? 0).toLocaleString())}
        {stat('toll', 'Credits used', loading ? '...' : fmtCredits(data?.totalCredits ?? 0))}
      </div>

      {error && (
        <div style={{ margin: '0 0 12px', padding: '9px 12px', borderRadius: 8, background: '#fdecea', color: '#c0392b', fontSize: 13 }}>{error}</div>
      )}

      {loading && !data ? (
        <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: '#8b96a3' }}>Loading...</div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <DataTable
            head={['When', 'Action', 'Action key', 'User', 'Credits']}
            align={['left', 'left', 'left', 'left', 'right']}
            rows={(data?.items ?? []).map(i => [
              fmtDateTime(i.at),
              actionLabel(i.actionKey),
              <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#4a5563' }}>{i.actionKey}</span>,
              displayUser(i.userRef) || <span style={{ color: '#9aa7b4' }}>Not recorded</span>,
              <span style={{ color: '#c0392b', fontWeight: 600 }}>-{fmtCredits(i.creditCost)}</span>,
            ])}
            empty={error ? 'The credit history could not be loaded.' : emptyText}
          />
        </div>
      )}

      {/* Pager */}
      {data && data.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13, color: '#4a5563' }}>
          <span>
            {((data.page - 1) * data.pageSize + 1).toLocaleString()} to {Math.min(data.page * data.pageSize, data.total).toLocaleString()} of {data.total.toLocaleString()}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-small" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              <span className="material-icons">chevron_left</span> Prev
            </button>
            <span>Page {data.page} of {totalPages}</span>
            <button className="btn-small" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>
              Next <span className="material-icons">chevron_right</span>
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

// ---- tenant activity log (UserActions), one month at a time ----

const ACTIONS_PAGE_SIZE = 50;

/** The last 12 months, newest first, as 'YYYY-MM' (UTC months, like the server). */
const lastMonths = (count: number = 12): { value: string; label: string }[] => {
  const now = new Date();
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push({
      value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    });
  }
  return out;
};

const CHARGE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'Charged', label: 'Charged' },
  { value: '', label: 'All statuses' },
  { value: 'NotBillable', label: 'Not billable' },
  { value: 'Pending', label: 'Pending' },
  { value: 'TenantNotFound', label: 'No matching tenant' },
];

const chargeStatusLabel = (s: string | null): string =>
  s === 'NotBillable' ? 'Not billable' : s === 'TenantNotFound' ? 'No matching tenant' : s || 'None';

const chargeStatusPill = (s: string | null): React.CSSProperties => {
  const [bg, fg] =
    s === 'Charged' ? ['#e7f6ec', '#1e7e46']
    : s === 'Pending' ? ['#fff4e0', '#9a6200']
    : s === 'TenantNotFound' ? ['#fdecea', '#c0392b']
    : ['#f1f4f7', '#4a5563'];
  return { background: bg, color: fg, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };
};

const TenantUserActions: React.FC<{ tenant: api.AdminTenantRow }> = ({ tenant }) => {
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth] = useState(months[0].value);
  const [status, setStatus] = useState('Charged');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<api.TenantUserActionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    api.getTenantUserActions(tenant.id, month, status, page, ACTIONS_PAGE_SIZE)
      .then(r => { if (live) setData(r); })
      .catch(e => { if (live) { setData(null); setError(e?.message ?? String(e)); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tenant.id, month, status, page]);

  const monthLabel = months.find(m => m.value === month)?.label ?? month;
  const statusLabel = CHARGE_STATUS_OPTIONS.find(o => o.value === status)?.label ?? status;
  const allCount = data ? Object.values(data.statusCounts).reduce((a, b) => a + b, 0) : 0;
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  const field: React.CSSProperties = { padding: '7px 10px', border: '1px solid #cdd6e0', borderRadius: 7, fontSize: 13, background: '#fff' };
  const fl: React.CSSProperties = { display: 'block', fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 };
  const stat = (icon: string, label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 22px 4px 0' }}>
      <span className="material-icons" style={{ fontSize: 20, color: '#1d5fa8' }}>{icon}</span>
      <div>
        <div style={fl}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#173042', whiteSpace: 'nowrap' }}>{value}</div>
      </div>
    </div>
  );

  const emptyText = status
    ? `No ${statusLabel.toLowerCase()} actions in ${monthLabel}.`
    : `No actions in ${monthLabel}.`;

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        <label><span style={fl}>Month</span>
          <select style={field} value={month} onChange={e => { setMonth(e.target.value); setPage(1); }}>
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <label><span style={fl}>Charge status</span>
          <select style={field} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            {CHARGE_STATUS_OPTIONS.map(o => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 0 14px' }}>
        {stat('date_range', 'Month', monthLabel)}
        {stat('bolt', status ? statusLabel : 'Actions', loading ? '...' : (data?.total ?? 0).toLocaleString())}
        {stat('toll', 'Points used', loading ? '...' : fmtCredits(data?.totalPoints ?? 0))}
        {status && stat('list', 'All actions this month', loading ? '...' : allCount.toLocaleString())}
      </div>

      {data && data.daysFromArchive > 0 && (
        <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: '#eef5fc', color: '#1d5fa8', fontSize: 12 }}>
          {data.daysFromArchive} day(s) of this month are read from the S3 archive (already removed from the database).
        </div>
      )}

      {error && (
        <div style={{ margin: '0 0 12px', padding: '9px 12px', borderRadius: 8, background: '#fdecea', color: '#c0392b', fontSize: 13 }}>{error}</div>
      )}

      {loading && !data ? (
        <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: '#8b96a3' }}>Loading...</div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <DataTable
            head={['Created', 'Module', 'Action', 'Points', 'Charge status']}
            align={['left', 'left', 'left', 'right', 'left']}
            rows={(data?.items ?? []).map(i => [
              fmtDateTime(i.createdDateUtc),
              i.moduleName || <span style={{ color: '#9aa7b4' }}>Not set</span>,
              <span title={i.actionName}>{i.actionLabel}</span>,
              fmtCredits(i.pointsUsed),
              <span style={chargeStatusPill(i.chargeStatus)}>{chargeStatusLabel(i.chargeStatus)}</span>,
            ])}
            empty={error ? 'The user actions could not be loaded.' : emptyText}
            note="Months are UTC calendar months. Created is shown in your local time."
          />
        </div>
      )}

      {/* Pager */}
      {data && data.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13, color: '#4a5563' }}>
          <span>
            {((data.page - 1) * data.pageSize + 1).toLocaleString()} to {Math.min(data.page * data.pageSize, data.total).toLocaleString()} of {data.total.toLocaleString()}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-small" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              <span className="material-icons">chevron_left</span> Prev
            </button>
            <span>Page {data.page} of {totalPages}</span>
            <button className="btn-small" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>
              Next <span className="material-icons">chevron_right</span>
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

// ---- tenant prices: this tenant's own credit price for selected actions ----

const TenantPricing: React.FC<{ tenant: api.AdminTenantRow }> = ({ tenant }) => {
  const [data, setData] = useState<api.TenantPricesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [customOnly, setCustomOnly] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  /** The row being edited or reset, and its form values. */
  const [editing, setEditing] = useState<{ action: string; mode: 'edit' | 'reset'; points: string; reason: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.getTenantPrices(tenant.id)
      .then(r => setData(r))
      .catch(e => { setData(null); setError(e?.message ?? String(e)); })
      .finally(() => setLoading(false));
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.items ?? []).filter(i => {
    if (customOnly && !i.isCustom) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [i.actionName, i.moduleName ?? '', i.functionName ?? ''].some(v => v.toLowerCase().includes(q));
  });

  const startEdit = (i: api.TenantPriceItem) => {
    setNotice(''); setFormError('');
    setEditing({ action: i.actionName, mode: 'edit', points: String(i.tenantPoints ?? i.defaultPoints), reason: '' });
  };
  const startReset = (i: api.TenantPriceItem) => {
    setNotice(''); setFormError('');
    setEditing({ action: i.actionName, mode: 'reset', points: '', reason: '' });
  };

  const save = async () => {
    if (!editing) return;
    setFormError('');
    try {
      setSaving(true);
      if (editing.mode === 'edit') {
        const points = Number(editing.points);
        if (editing.points.trim() === '' || !isFinite(points) || points < 0) { setFormError('Enter points of 0 or more.'); return; }
        if (editing.reason.trim().length < 5) { setFormError('Enter a reason (at least 5 characters).'); return; }
        await api.setTenantPrice(tenant.id, editing.action, points, editing.reason.trim());
        setNotice(`${editing.action} now costs ${fmtCredits(points)} credits for ${tenant.name}. New actions use it within 10 minutes.`);
      } else {
        await api.resetTenantPrice(tenant.id, editing.action);
        setNotice(`${editing.action} is back to the default price for ${tenant.name}. New actions use it within 10 minutes.`);
      }
      setEditing(null);
      load();
    } catch (e: any) {
      setFormError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  };

  const field: React.CSSProperties = { padding: '7px 10px', border: '1px solid #cdd6e0', borderRadius: 7, fontSize: 13, background: '#fff' };
  const fl: React.CSSProperties = { display: 'block', fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 };
  const customTag: React.CSSProperties = { background: '#e8f1fb', color: '#1d5fa8', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, marginLeft: 6 };

  /** The row being edited, looked up again from the loaded list. */
  const editItem = editing ? (data?.items ?? []).find(i => i.actionName === editing.action) ?? null : null;
  const editorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (editing) editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [editing?.action, editing?.mode]);

  const editor = (i: api.TenantPriceItem) => editing && (
    <div ref={editorRef} style={{
      margin: '0 0 14px', padding: 14, borderRadius: 10, background: '#f0f7fd',
      border: '2px solid #1d5fa8', borderLeftWidth: 6, boxShadow: '0 0 0 3px rgba(29,95,168,.15)',
    }}>
      {/* Which action is being changed, in full */}
      <div style={{ fontWeight: 700, fontSize: 15, color: '#173042', marginBottom: 10 }}>
        {editing.mode === 'edit' ? (i.isCustom ? 'Change own price' : 'Set own price') : 'Use the default price'} for {tenant.name}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10,
        marginBottom: 14, padding: '10px 12px', background: '#fff', borderRadius: 8, border: '1px solid #dbe6f1',
      }}>
        {([
          ['Action name', i.actionName],
          ['Function name', i.functionName || '-'],
          ['Module', i.moduleName || '-'],
          ['Default price', `${fmtCredits(i.defaultPoints)} credits`],
          ['This tenant now', i.isCustom ? `${fmtCredits(i.tenantPoints)} credits` : 'Default'],
        ] as [string, string][]).map(([label, value]) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: '#6b7684', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#173042', overflowWrap: 'anywhere' }}>{value}</div>
          </div>
        ))}
      </div>
      {editing.mode === 'edit' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label><span style={fl}>New points for this tenant</span>
            <input style={{ ...field, width: 130 }} type="number" min={0} step="0.01" value={editing.points} autoFocus
                   onChange={e => setEditing({ ...editing, points: e.target.value })} />
          </label>
          <label style={{ flex: 1, minWidth: 220 }}><span style={fl}>Reason</span>
            <input style={{ ...field, width: '100%' }} maxLength={300} placeholder="e.g. Agreed rate in the 2026 contract"
                   value={editing.reason} onChange={e => setEditing({ ...editing, reason: e.target.value })} />
          </label>
          <button className="btn-small btn-small--primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
          <button className="btn-small btn-small--ghost" disabled={saving} onClick={() => setEditing(null)}>Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#173042', flex: 1 }}>
            Remove this tenant's own price? It will pay the default of {fmtCredits(i.defaultPoints)} credits.
          </span>
          <button className="btn-small btn-small--primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Use default'}</button>
          <button className="btn-small btn-small--ghost" disabled={saving} onClick={() => setEditing(null)}>Cancel</button>
        </div>
      )}
      {formError && <div style={{ marginTop: 8, color: '#c0392b', fontSize: 12 }}>{formError}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ margin: '0 0 12px', fontSize: 13, color: '#4a5563' }}>
        Actions without an own price use the default from Pricing and Credits. An own price applies to new actions
        within 10 minutes. Deactivating an action in Pricing and Credits makes it free for every tenant.
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
        <label><span style={fl}>Search</span>
          <input style={field} placeholder="Action or module" value={search} onChange={e => setSearch(e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#173042', paddingBottom: 8 }}>
          <input type="checkbox" checked={customOnly} onChange={e => setCustomOnly(e.target.checked)} /> Own prices only
        </label>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#4a5563', paddingBottom: 8 }}>
          {data ? `${data.customCount} own price(s) of ${data.items.length} actions` : ''}
        </span>
      </div>

      {notice && (
        <div style={{ margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, background: '#e7f6ec', color: '#1e7e46', fontSize: 13 }}>{notice}</div>
      )}
      {error && (
        <div style={{ margin: '0 0 12px', padding: '9px 12px', borderRadius: 8, background: '#fdecea', color: '#c0392b', fontSize: 13 }}>{error}</div>
      )}

      {editItem && editor(editItem)}

      {loading && !data ? (
        <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: '#8b96a3' }}>Loading...</div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <DataTable
            head={['Module', 'Action name', 'Function name', 'Default', 'This tenant', '']}
            align={['left', 'left', 'left', 'right', 'right', 'right']}
            rows={rows.map(i => [
              i.moduleName || <span style={{ color: '#9aa7b4' }}>Not set</span>,
              <div>
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{i.actionName}</span>
                {i.isCustom && <span style={customTag}>Own price</span>}
                {editing?.action === i.actionName && <span style={{ ...customTag, background: '#fff4e0', color: '#9a6200' }}>Editing</span>}
                {i.isCustom && i.reason && (
                  <div style={{ fontSize: 11, color: '#6b7684', marginTop: 2 }}>
                    {i.reason}{i.changedBy ? ` (${i.changedBy}, ${fmtDateTime(i.changedDateUtc)})` : ''}
                  </div>
                )}
              </div>,
              i.functionName || <span style={{ color: '#9aa7b4' }}>Not set</span>,
              fmtCredits(i.defaultPoints),
              i.isCustom
                ? <strong style={{ color: '#1d5fa8' }}>{fmtCredits(i.tenantPoints)}</strong>
                : <span style={{ color: '#9aa7b4' }}>Default</span>,
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button className="btn-small" disabled={saving} onClick={() => startEdit(i)}>{i.isCustom ? 'Change' : 'Set price'}</button>
                {i.isCustom && <button className="btn-small btn-small--ghost" disabled={saving} onClick={() => startReset(i)}>Use default</button>}
              </span>,
            ])}
            empty={error ? 'The prices could not be loaded.' : customOnly ? 'This tenant has no own prices.' : 'No active actions in the price list.'}
          />
        </div>
      )}

      {/* Change history */}
      {data && data.history.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button className="btn-small btn-small--ghost" onClick={() => setShowHistory(v => !v)}>
            <span className="material-icons">{showHistory ? 'expand_less' : 'expand_more'}</span>
            Change history ({data.history.length})
          </button>
          {showHistory && (
            <div style={{ marginTop: 8 }}>
              <DataTable
                head={['Set on', 'Action name', 'Points', 'Reason', 'Status']}
                align={['left', 'left', 'right', 'left', 'left']}
                rows={data.history.map(h => [
                  <div>{fmtDateTime(h.createdDateUtc)}<div style={{ fontSize: 11, color: '#6b7684' }}>{h.createdBy ?? ''}</div></div>,
                  h.actionName,
                  fmtCredits(h.points),
                  h.reason ?? '',
                  h.isActive
                    ? <span style={pillStyle('completed')}>In use</span>
                    : <span style={pillStyle('')}>Replaced {h.modifiedDateUtc ? fmtDateTime(h.modifiedDateUtc) : ''}</span>,
                ])}
                empty="No changes yet."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
